import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import type {TBBox} from '@postermywall/fabricjs-2';
import {MARQUEE_DRAG_THRESHOLD_PX} from '@PosterWhiteboard/page/page.types';
import {PosterGestures} from '@PosterWhiteboard/poster/poster-gestures.class';
import {getFabricObjectUID} from '@PosterWhiteboard/items/item/item.class';
import {isEditorMobileVariant} from '@Libraries/is-editor-mobile-variant';
import {updateMarqueePreviewItems} from '@Components/poster-editor/poster-editor-reducer';
import {useAppDispatch, useAppSelector} from '@/hooks';
import type {MarqueeDragSession} from './marquee-selection-layer.types';
import {
  applyMarqueeSelection,
  clientPointToScenePoint,
  createMarqueeSessionForMouseDown,
  getSceneRectFromCorners,
  getWhiteboardContainer,
  reselectBackgroundAfterDriftClick,
  setMarqueeDragSelectInProgressOnAllPages,
} from './marquee-selection-layer-library';
import styles from './marquee-selection-layer.module.scss';

/**
 * DOM-level replacement for fabric's canvas-drawn marquee (drag-to-multiselect). Owning the
 * gesture outside the canvas lets a marquee start and draw in the gray whiteboard area, which
 * fabric's group selector cannot do. Hit-testing and selection stay in the poster-whiteboard
 * domain via SelectionManager.selectItemsIntersectingSceneRect.
 */
export function MarqueeSelectionLayer(): ReactElement | null {
  const [isSessionActive, setIsSessionActive] = useState(false);
  const sessionRef = useRef<MarqueeDragSession | null>(null);
  const rectRef = useRef<HTMLDivElement | null>(null);
  const lastPreviewIdsKeyRef = useRef('');
  const dispatch = useAppDispatch();
  const pageOrder = useAppSelector((state) => {
    return state.posterEditor?.posterObject?.pages.pageOrder;
  });
  const isVersionHistoryModeActive = useAppSelector((state) => {
    return state.posterEditor.isVersionHistoryModeActive;
  });
  const hasPages = Boolean(pageOrder?.length);

  useEffect(() => {
    const poster = window.posterEditor?.whiteboard;
    const container = getWhiteboardContainer();
    if (!poster || !container || !hasPages || isVersionHistoryModeActive) {
      return;
    }
    if (poster.mode.isWebpage() || poster.mode.isGeneration() || isEditorMobileVariant() || PosterGestures.shouldEnableGestures()) {
      return;
    }

    const onContainerMouseDown = (e: MouseEvent): void => {
      const whiteboard = window.posterEditor?.whiteboard;
      if (!whiteboard || sessionRef.current || e.button !== 0 || whiteboard.drawing.isDrawModeOn) {
        return;
      }
      const session = createMarqueeSessionForMouseDown(whiteboard, e);
      if (!session) {
        return;
      }
      if (!session.startedOnCanvas) {
        e.preventDefault();
      }
      sessionRef.current = session;
      setIsSessionActive(true);
    };

    container.addEventListener('mousedown', onContainerMouseDown);
    return (): void => {
      container.removeEventListener('mousedown', onContainerMouseDown);
    };
  }, [hasPages, isVersionHistoryModeActive]);

  useEffect(() => {
    if (!isSessionActive) {
      return;
    }
    const container = getWhiteboardContainer();

    // Hover boxes on every item the box currently covers — the live preview of what a mouseup
    // would select. Dispatches only when the covered set changes, not per mousemove.
    const updateMarqueePreview = (session: MarqueeDragSession, sceneRect: TBBox): void => {
      const previewItemIds = session.startPage.activeSelection.getItemsIntersectingSceneRect(sceneRect).map(getFabricObjectUID);
      const previewKey = previewItemIds.join(',');
      if (previewKey === lastPreviewIdsKeyRef.current) {
        return;
      }
      lastPreviewIdsKeyRef.current = previewKey;
      dispatch(updateMarqueePreviewItems({pageHashedId: session.startPage.hashedID, itemIds: previewItemIds}));
    };

    const updateMarqueeRect = (): void => {
      const session = sessionRef.current;
      const rectElement = rectRef.current;
      const poster = window.posterEditor?.whiteboard;
      if (!session || !rectElement || !container || !poster) {
        return;
      }
      const zoom = poster.scaling.scale;
      const backdropRect = session.startPage.canvasBackdrop.getBoundingClientRect();
      const sceneRect = getSceneRectFromCorners(session.anchorScene, clientPointToScenePoint(session.lastClient, backdropRect, zoom));
      const containerRect = container.getBoundingClientRect();
      rectElement.style.display = 'block';
      rectElement.style.left = `${backdropRect.left - containerRect.left + container.scrollLeft + sceneRect.left * zoom}px`;
      rectElement.style.top = `${backdropRect.top - containerRect.top + container.scrollTop + sceneRect.top * zoom}px`;
      rectElement.style.width = `${sceneRect.width * zoom}px`;
      rectElement.style.height = `${sceneRect.height * zoom}px`;
      updateMarqueePreview(session, sceneRect);
    };

    const confirmDragSelect = (session: MarqueeDragSession): void => {
      const poster = window.posterEditor?.whiteboard;
      if (!poster) {
        return;
      }
      session.didPassThreshold = true;
      setMarqueeDragSelectInProgressOnAllPages(true);
      if (!session.startedOnCanvas) {
        poster.setCurrentPage(session.startPage.hashedID);
      }
      if (poster.isPlaying()) {
        void poster.pause();
      }
    };

    const onWindowMouseMove = (e: MouseEvent): void => {
      const session = sessionRef.current;
      if (!session) {
        return;
      }
      session.lastClient = {x: e.clientX, y: e.clientY};
      if (!session.didPassThreshold) {
        if (Math.abs(e.clientX - session.startClient.x) < MARQUEE_DRAG_THRESHOLD_PX && Math.abs(e.clientY - session.startClient.y) < MARQUEE_DRAG_THRESHOLD_PX) {
          return;
        }
        confirmDragSelect(session);
      }
      updateMarqueeRect();
    };

    // Runs after fabric's document-level mouseup and Page.onMouseUp (window is last in the bubble
    // path), so fabric's own click handling is settled before the marquee selection is applied.
    const onWindowMouseUp = (e: MouseEvent): void => {
      const session = sessionRef.current;
      if (!session) {
        return;
      }
      if (session.didPassThreshold) {
        applyMarqueeSelection(session, e);
      } else {
        reselectBackgroundAfterDriftClick(session, e);
      }
      setIsSessionActive(false);
    };

    const onWindowKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setIsSessionActive(false);
      }
    };

    const onWindowBlur = (): void => {
      setIsSessionActive(false);
    };

    const onContainerScroll = (): void => {
      if (sessionRef.current?.didPassThreshold) {
        updateMarqueeRect();
      }
    };

    window.addEventListener('mousemove', onWindowMouseMove);
    window.addEventListener('mouseup', onWindowMouseUp);
    window.addEventListener('keydown', onWindowKeyDown);
    window.addEventListener('blur', onWindowBlur);
    container?.addEventListener('scroll', onContainerScroll);

    return (): void => {
      window.removeEventListener('mousemove', onWindowMouseMove);
      window.removeEventListener('mouseup', onWindowMouseUp);
      window.removeEventListener('keydown', onWindowKeyDown);
      window.removeEventListener('blur', onWindowBlur);
      container?.removeEventListener('scroll', onContainerScroll);
      const session = sessionRef.current;
      sessionRef.current = null;
      if (session && lastPreviewIdsKeyRef.current !== '') {
        lastPreviewIdsKeyRef.current = '';
        dispatch(updateMarqueePreviewItems({pageHashedId: session.startPage.hashedID, itemIds: []}));
      }
      if (session?.didPassThreshold) {
        setMarqueeDragSelectInProgressOnAllPages(false);
      }
    };
  }, [isSessionActive, dispatch]);

  if (!isSessionActive) {
    return null;
  }
  const container = getWhiteboardContainer();
  if (!container) {
    return null;
  }
  return createPortal(<div ref={rectRef} className={styles.marqueeRect} />, container);
}
